import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Page } from "@models/page";
import {
  ApplyCouponResponse,
  Coupon,
  CouponCreatePayload,
  CouponListFilters,
  CouponPage,
  CouponRedemption,
  CouponUpdatePayload,
} from "@models/coupon";
import { catchError, Observable, of } from "rxjs";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class CouponsService {
  private readonly _http = inject(HttpClient);
  private readonly apiUrl = environment.API_BACKEND;

  // Rotas de gestão por evento (doc §3): aninhadas no evento.
  private eventBaseUrl(eventId: number): string {
    return `${this.apiUrl}/events/${eventId}/coupons`;
  }

  // Lista global de todos os cupons do produtor (novo endpoint do BE:
  // getAllEventsCupons). Mesmos filtros/ordenação da listagem por evento.
  listAll(filters: CouponListFilters = {}): Observable<CouponPage> {
    return this.listFromUrl(`${this.apiUrl}/coupons`, filters);
  }

  list(
    eventId: number,
    filters: CouponListFilters = {},
  ): Observable<CouponPage> {
    return this.listAll({ ...filters, event_id: eventId });
  }

  private listFromUrl(
    url: string,
    filters: CouponListFilters,
  ): Observable<CouponPage> {
    let params = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params = params.set(key, String(value));
      }
    });

    return this._http.get<CouponPage>(url, { params }).pipe(
      // Listagem vazia / filtros sem resultado: tratar como "nenhum cupom".
      catchError(() =>
        of({
          data: [],
          meta: {
            total_count: 0,
            total_pages: 0,
            current_page: 0,
            page_size: filters.per_page ?? 10,
            summary: { created: 0, active: 0, closed: 0, cancelled: 0 },
          },
        } as CouponPage),
      ),
    );
  }

  get(eventId: number, id: number): Observable<Coupon> {
    return this._http.get<Coupon>(`${this.eventBaseUrl(eventId)}/${id}`);
  }

  create(eventId: number, payload: CouponCreatePayload): Observable<Coupon> {
    return this._http.post<Coupon>(this.eventBaseUrl(eventId), {
      coupon: payload,
    });
  }

  update(
    eventId: number,
    id: number,
    payload: CouponUpdatePayload,
  ): Observable<Coupon> {
    return this._http.patch<Coupon>(`${this.eventBaseUrl(eventId)}/${id}`, {
      coupon: payload,
    });
  }

  // Desativar = cancelar. Só sem uso; com uso a API retorna 422 (oferecer
  // Encerrar).
  deactivate(eventId: number, id: number): Observable<Coupon> {
    return this._http.post<Coupon>(
      `${this.eventBaseUrl(eventId)}/${id}/deactivate`,
      {},
    );
  }

  // Encerrar = pausa mantendo o histórico de quem já usou.
  close(eventId: number, id: number): Observable<Coupon> {
    return this._http.post<Coupon>(
      `${this.eventBaseUrl(eventId)}/${id}/close`,
      {},
    );
  }

  redemptions(
    eventId: number,
    id: number,
    page = 1,
    perPage = 20,
  ): Observable<Page<CouponRedemption>> {
    const params = new HttpParams().set("page", page).set("per_page", perPage);

    return this._http.get<Page<CouponRedemption>>(
      `${this.eventBaseUrl(eventId)}/${id}/redemptions`,
      { params },
    );
  }

  downloadUsageReport(eventId: number, id: number): Observable<Blob> {
    return this._http.get(`${this.eventBaseUrl(eventId)}/${id}/usage_report`, {
      responseType: "blob",
    });
  }

  // --- checkout (Fase G) ---
  // `ticketId` = ingresso onde aplicar o desconto. Sem ele, o backend usa o de
  // maior valor. Só há escolha real em cupons antigos multi-categoria.
  applyToCart(
    code: string,
    ticketId?: number,
  ): Observable<ApplyCouponResponse> {
    const body: { code: string; ticket_id?: number } = { code };
    if (ticketId != null) {
      body.ticket_id = ticketId;
    }
    return this._http.post<ApplyCouponResponse>(
      `${this.apiUrl}/cart/apply_coupon`,
      body,
    );
  }

  removeFromCart(): Observable<{ message: string }> {
    return this._http.delete<{ message: string }>(
      `${this.apiUrl}/cart/remove_coupon`,
    );
  }
}
